import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Minus, Plus } from "lucide-react";

import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { UnifiedModal } from "../../components/UnifiedModal";
import { formatDate, formatPrice, pluralize } from "./billingFormat";

const MAX_EXTRA_USERS = 100;

/**
 * Plazas de usuario adicionales, en una suscripción de Stripe aparte de la del plan. Aquí solo se elige la
 * cantidad: Stripe muestra el importe y cobra (Checkout sin plazas; confirmación del portal con ellas).
 */
export const ExtraUsersDialog = ({ open, onClose, billing, price, onConfirm }) => {
  const theme = useTheme();
  const { extra_users: current, included_users: included, users_count: usersCount, seats } = billing;
  const scheduled = billing.scheduled_extra_users;
  const seatsLive = seats.has_subscription;
  const seatsEnd = formatDate(seats.current_period_end);

  const [quantity, setQuantity] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setQuantity(seatsLive ? current : Math.max(current, 1));
    } else {
      setSaving(false);
      setError("");
    }
  }, [open, current, seatsLive]);

  const floor = seatsLive ? 0 : 1;
  const decrease = quantity < current;
  const leftOver = decrease ? usersCount - (included + quantity) : 0;
  // Cualquier otra cantidad sustituye la bajada de plazas programada; al subir, Stripe lo hace sin avisar
  const replacesScheduled = scheduled != null && quantity !== scheduled && quantity !== current;

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      if (!(await onConfirm(quantity))) setSaving(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "No se han podido actualizar las plazas. Inténtalo de nuevo.");
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const infoText = (() => {
    if (!seatsLive) return "Podrás invitar a más miembros en cuanto se complete el pago.";
    if (quantity === 0) return `Tus plazas adicionales se mantienen hasta el ${seatsEnd} y después dejan de cobrarse.`;
    if (decrease) return `Hasta el ${seatsEnd} mantienes tus plazas actuales; después tendrás menos. Lo que ya has pagado no se devuelve.`;
    return "Hoy solo pagas la parte proporcional hasta tu próxima renovación. Las plazas nuevas se activan en cuanto se completa el pago.";
  })();

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      title="Usuarios adicionales"
      disableBackdropClose={saving}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving || (seatsLive && quantity === current) || (seats.cancel_at_period_end && quantity === 0)}>
            {saving ? "Redirigiendo…" : decrease ? "Continuar" : "Ir al pago"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Tu plan incluye {pluralize(included, "usuario", "usuarios")}. Cada plaza adicional permite un administrador o
          consultor más por {formatPrice(price)} /mes.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outline"
            size="icon"
            aria-label="Quitar una plaza"
            disabled={saving || quantity <= floor}
            onClick={() => setQuantity((value) => value - 1)}
          >
            <Minus size={16} />
          </Button>
          <Box sx={{ minWidth: 96, textAlign: "center" }}>
            <Typography variant="h4" sx={{ fontWeight: 750, lineHeight: 1.1 }}>{quantity}</Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {quantity === 1 ? "plaza adicional" : "plazas adicionales"}
            </Typography>
          </Box>
          <Button
            variant="outline"
            size="icon"
            aria-label="Añadir una plaza"
            disabled={saving || quantity >= MAX_EXTRA_USERS}
            onClick={() => setQuantity((value) => value + 1)}
          >
            <Plus size={16} />
          </Button>
        </Box>

        <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 600 }}>
          Total: {pluralize(included + quantity, "usuario", "usuarios")}
        </Typography>

        <AlertNote variant="info">{infoText}</AlertNote>
        {replacesScheduled && (
          <AlertNote variant="warning">
            Tienes programado pasar a {pluralize(scheduled, "plaza adicional", "plazas adicionales")} el {seatsEnd}. Si
            continúas, este cambio reemplaza al anterior.
          </AlertNote>
        )}
        {leftOver > 0 && (
          <AlertNote variant="warning">
            Tienes {pluralize(usersCount, "usuario", "usuarios")} y te quedarán {included + quantity} plazas. Cuando se
            aplique la bajada tendrás que elegir quién deja el equipo. Si quieres quitar a alguien antes, desactívalo desde
            Equipo y su plaza quedará libre para otra persona.
          </AlertNote>
        )}
        {error && <AlertNote variant="danger">{error}</AlertNote>}
      </Stack>
    </UnifiedModal>
  );
};

ExtraUsersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  billing: PropTypes.object.isRequired,
  // Requerido para abrirlo; cerrado puede faltar mientras carga la facturación
  price: PropTypes.object,
  // useStripeRedirect().go: false si la agencia se queda en la página
  onConfirm: PropTypes.func.isRequired,
};
